import { Box, Grid, Prose, Spinner } from '@factorialco/deprecated-design-system'
import { PerformanceTeamInsightsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AiFeedbackRequest from 'modules/ai/components/FeedbackRequest'
import { AiTag } from 'modules/performance_process/components/insights/AiTag'
import { InsightsEmptyState } from 'modules/performance_process/components/insights/EmptyState'
import { scoreTranslations } from 'modules/performance_process/components/insights/TargetCard'
import { InsightMetricCard } from 'modules/performance_process/components/insights/common/InsightMetricCard'
import { InsightSummary } from 'modules/performance_process/components/insights/common/InsightSummary'

export const TeamInsights: React.FC<{
  reviewId: number
  forAccessId: number
  noBorder?: boolean
}> = ({ reviewId, forAccessId, noBorder = false }) => {
  const { data, loading, error } = useQuery(PerformanceTeamInsightsDocument, {
    variables: {
      reviewId: Number(reviewId),
      forAccessId: forAccessId,
    },
  })

  const managerInsight = data?.performance.managerInsightsConnection.nodes[0]
  const teamSummary = managerInsight?.generalSummary
  const actionItems = managerInsight?.actionItems

  const hasInsights = !!managerInsight && !managerInsight.empty

  const EmptyState = () => {
    if (managerInsight?.empty) {
      return <InsightsEmptyState type="noData" noBorder={noBorder} />
    }
    if (data?.performance.reviewProcess?.migratedFromV1) {
      return <InsightsEmptyState type="migratedFromV1" noBorder={noBorder} />
    }
    return <InsightsEmptyState type="inProgress" noBorder={noBorder} />
  }

  if (loading || error) {
    return (
      <Box flexGrow justifyContent="center" alignItems="center" paddingY="s48" height="full">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box gap="s32" flexGrow>
      {hasInsights ? (
        <>
          <Box gap="s16">
            <AiTag />
            <Grid variant="fixed">
              {managerInsight?.averagePerformanceScore && (
                <InsightMetricCard
                  title={i18n.t('performance_process.targets.insights.performance')}
                  description={scoreTranslations(managerInsight.averagePerformanceScore)}
                  value={managerInsight.averagePerformanceScore}
                  hasLink={false}
                />
              )}
              {managerInsight?.averageAlignmentScore && (
                <InsightMetricCard
                  title={i18n.t('performance_process.targets.insights.alignment')}
                  description={scoreTranslations(managerInsight.averageAlignmentScore)}
                  value={managerInsight.averageAlignmentScore}
                  hasLink={false}
                />
              )}
            </Grid>
          </Box>
          {teamSummary && (
            <InsightSummary title={i18n.t('performance_process.targets.ai_summary')}>
              <Prose fullWidth>{teamSummary}</Prose>
            </InsightSummary>
          )}
          {!!actionItems?.length && (
            <InsightSummary
              title={i18n.t('performance_process.insights.titles.suggested_action_items')}
            >
              <Prose fullWidth>
                <ul>
                  {actionItems.map((actionItem, index) => (
                    <li key={index}>{actionItem}</li>
                  ))}
                </ul>
              </Prose>
            </InsightSummary>
          )}

          <Box alignSelf="flexEnd">
            <Prose fullWidth>
              <AiFeedbackRequest
                feedbackType="performance.manager_insight.summary"
                resourceId={managerInsight.id}
              />
            </Prose>
          </Box>
        </>
      ) : (
        <EmptyState />
      )}
    </Box>
  )
}
